import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const GameContainer = styled.div`
  padding: 40px 20px;
  text-align: center;
`;

const GameTitle = styled.h2`
  font-size: 2.5em;
  color: #ff6b6b;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.3);
  margin-bottom: 20px;
  animation: ${bounce} 2s ease-in-out infinite;
  font-family: 'Comic Sans MS', cursive, sans-serif;
`;

const StartButton = styled.button`
  padding: 15px 40px;
  font-size: 1.3em;
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: white;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s;
  font-family: 'Comic Sans MS', cursive, sans-serif;
  font-weight: bold;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 7px 20px rgba(0, 0, 0, 0.4);
  }
`;

const GameStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
  margin: 20px 0;
  font-size: 1.3em;
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const GameCanvas = styled.canvas`
  width: 100%;
  max-width: 400px;
  height: 600px;
  background: linear-gradient(to bottom, #87ceeb, #e0f6ff);
  border: 5px solid white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: block;
  margin: 0 auto;
  cursor: pointer;

  @media (max-width: 768px) {
    height: 550px;
  }

  @media (max-width: 480px) {
    height: 500px;
  }
`;

const GameOver = styled.div`
  padding: 30px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 20px;
  color: white;
  max-width: 400px;
  margin: 0 auto;

  h3 {
    font-size: 2em;
    margin-bottom: 15px;
    color: #ff6b6b;
  }

  p {
    font-size: 1.2em;
    margin: 10px 0;
  }
`;

const Instructions = styled.p`
  font-size: 1.1em;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin: 20px 0;
`;

function FlappySteven() {
  const canvasRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);

  const gameStateRef = useRef({
    steven: { x: 80, y: 250, width: 50, height: 50, velocity: 0, gravity: 0.4, jump: -7 },
    pipes: [],
    animationId: null,
    lastPipeTime: 0,
    pipeInterval: 2000,
    pipeGap: 180,
    pipeWidth: 60,
    pipeSpeed: 2.5,
    frameCount: 0,
    stevenImg: null
  });

  useEffect(() => {
    // Load Steven's image - randomly pick one
    const img = new Image();
    img.src = Math.random() > 0.5 ? '/frolke2.jpg' : '/frolke.jpg';
    img.onload = () => {
      gameStateRef.current.stevenImg = img;
    };
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const state = gameStateRef.current;

    // Initialize game
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    state.steven.x = 80;
    state.steven.y = canvas.height / 2;
    state.steven.velocity = 0;
    state.pipes = [];
    state.lastPipeTime = 0;
    state.frameCount = 0;

    // Click/Tap to flap
    const handleFlap = (e) => {
      e.preventDefault();
      state.steven.velocity = state.steven.jump;
    };

    canvas.addEventListener('click', handleFlap);
    canvas.addEventListener('touchstart', handleFlap);

    // Space bar to flap
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        state.steven.velocity = state.steven.jump;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Game loop
    const gameLoop = () => {
      state.frameCount++;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update Steven's position
      state.steven.velocity += state.steven.gravity;
      state.steven.y += state.steven.velocity;

      // Check if Steven hit ground or ceiling
      if (state.steven.y + state.steven.height > canvas.height || state.steven.y < 0) {
        endGame();
        return;
      }

      // Spawn pipes
      const now = Date.now();
      if (now - state.lastPipeTime > state.pipeInterval) {
        state.lastPipeTime = now;
        const minHeight = 50;
        const maxHeight = canvas.height - state.pipeGap - 50;
        const topHeight = minHeight + Math.random() * (maxHeight - minHeight);
        
        state.pipes.push({
          x: canvas.width,
          topHeight: topHeight,
          bottomY: topHeight + state.pipeGap,
          scored: false
        });
      }

      // Update and draw pipes
      ctx.fillStyle = '#2ecc71';
      state.pipes.forEach((pipe, index) => {
        pipe.x -= state.pipeSpeed;

        // Draw top pipe
        ctx.fillRect(pipe.x, 0, state.pipeWidth, pipe.topHeight);
        // Draw bottom pipe
        ctx.fillRect(pipe.x, pipe.bottomY, state.pipeWidth, canvas.height - pipe.bottomY);

        // Collision detection with padding for forgiveness
        const padding = 5; // pixels of tolerance
        if (
          state.steven.x + state.steven.width - padding > pipe.x &&
          state.steven.x + padding < pipe.x + state.pipeWidth &&
          (state.steven.y + padding < pipe.topHeight || state.steven.y + state.steven.height - padding > pipe.bottomY)
        ) {
          endGame();
          return;
        }

        // Score when passing pipe
        if (!pipe.scored && pipe.x + state.pipeWidth < state.steven.x) {
          pipe.scored = true;
          setScore(prev => prev + 1);
        }

        // Remove off-screen pipes
        if (pipe.x + state.pipeWidth < 0) {
          state.pipes.splice(index, 1);
        }
      });

      // Draw Steven (with image if loaded, otherwise circle)
      if (state.stevenImg && state.stevenImg.complete) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(
          state.steven.x + state.steven.width / 2,
          state.steven.y + state.steven.height / 2,
          state.steven.width / 2,
          0,
          Math.PI * 2
        );
        ctx.clip();
        ctx.drawImage(
          state.stevenImg,
          state.steven.x,
          state.steven.y,
          state.steven.width,
          state.steven.height
        );
        ctx.restore();
        
        // Add border
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(
          state.steven.x + state.steven.width / 2,
          state.steven.y + state.steven.height / 2,
          state.steven.width / 2,
          0,
          Math.PI * 2
        );
        ctx.stroke();
      } else {
        ctx.fillStyle = '#ff6b6b';
        ctx.beginPath();
        ctx.arc(
          state.steven.x + state.steven.width / 2,
          state.steven.y + state.steven.height / 2,
          state.steven.width / 2,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }

      state.animationId = requestAnimationFrame(gameLoop);
    };

    const endGame = () => {
      setIsActive(false);
      setGameOver(true);
      if (score > highScore) {
        setHighScore(score);
      }
      if (state.animationId) {
        cancelAnimationFrame(state.animationId);
      }
    };

    gameLoop();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      canvas.removeEventListener('click', handleFlap);
      canvas.removeEventListener('touchstart', handleFlap);
      if (state.animationId) {
        cancelAnimationFrame(state.animationId);
      }
    };
  }, [isActive]);

  const startGame = () => {
    setIsActive(true);
    setGameOver(false);
    setScore(0);
  };

  return (
    <GameContainer id="flappy-steven">
      <GameTitle>🦅 FLAPPY STEVEN 🦅</GameTitle>
      
      {!isActive && !gameOver && (
        <>
          <Instructions>
            Click, tap, or press SPACE to make Steven fly!<br />
            Avoid the green pipes!
          </Instructions>
          <StartButton onClick={startGame}>START FLYING!</StartButton>
        </>
      )}

      {isActive && (
        <>
          <GameStats>
            <span>📊 Score: {score}</span>
            <span>🏆 Best: {highScore}</span>
          </GameStats>
          <GameCanvas ref={canvasRef} />
        </>
      )}

      {gameOver && (
        <GameOver>
          <h3>💥 CRASH! 💥</h3>
          <p>Score: {score}</p>
          <p>High Score: {highScore}</p>
          <StartButton onClick={startGame} style={{ marginTop: '20px' }}>
            TRY AGAIN!
          </StartButton>
        </GameOver>
      )}
    </GameContainer>
  );
}

export default FlappySteven;
